import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";

interface DiscardConfirmDialogProps {
  open: boolean;
  /** Dismiss the confirm and return to the form with edits intact. */
  onKeepEditing: () => void;
  /** Throw the edits away and run the caller's close handler. */
  onDiscard: () => void;
}

/**
 * Confirmation shown when a form modal is closed with unsaved edits.
 *
 * Pair with `useDiscardGuard`, which owns the open state and the dirty gate:
 * this component only renders. Sibling of `DeleteConfirmDialog` — same Radix
 * `AlertDialog` shell, so it portals above the form modal and traps focus.
 */
export function DiscardConfirmDialog({
  open,
  onKeepEditing,
  onDiscard,
}: DiscardConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onKeepEditing()}>
      <AlertDialogContent className="text-foreground">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">
            Discard Changes
          </AlertDialogTitle>
          <AlertDialogDescription className="text-foreground">
            You have unsaved changes. Are you sure you want to discard them?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onKeepEditing}>
            Keep Editing
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onDiscard}
            className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
          >
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Gates a modal's close paths behind `DiscardConfirmDialog` when `isDirty`.
 *
 * Route every close path through `requestClose` — the footer Cancel button and
 * the `Modal`'s `onClose` (which covers the X icon, Escape, and the backdrop) —
 * so a dirty form cannot be closed without confirming. When clean, `requestClose`
 * closes immediately and the dialog never appears.
 *
 *   const { requestClose, confirmProps } = useDiscardGuard(isDirty, onCancel);
 *   <Modal onClose={requestClose} footer={<ModalFooterButtons onCancel={requestClose} ... />} />
 *   <DiscardConfirmDialog {...confirmProps} />
 */
export function useDiscardGuard(isDirty: boolean, onClose: () => void) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return {
    requestClose: () => (isDirty ? setConfirmOpen(true) : onClose()),
    confirmProps: {
      open: confirmOpen,
      onKeepEditing: () => setConfirmOpen(false),
      onDiscard: () => {
        setConfirmOpen(false);
        onClose();
      },
    },
  };
}
